import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import trout_filet from "../assets/trout_filet.webp";
import water from "../assets/water.jpg";
import plants from "../assets/plants.jpg";

const WhatYouGet = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const benefits = [
    {
      title: "Poisson frais",
      description: "Truite élevée sans métaux lourds ni produits chimiques",
      image: trout_filet,
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      title: "Fruits, légumes & plantes aromatiques",
      description: "Production bio continue de vos légumes préférés",
      image: plants,
      gradient: "from-emerald-500 to-green-500",
    },
    {
      title: "Utilisation intelligente de l'eau",
      description: "90% d'économie d'eau grâce au système en circuit fermé",
      image: water,
      gradient: "from-blue-400 to-teal-500",
    },
    {
      title: "Un retour sur investissement",
      description: "Amortissement en 12-18 mois avec des économies continues",
      image: "https://images.unsplash.com/photo-1492496913980-501348b61469",
      gradient: "from-amber-500 to-orange-500",
    },
  ];

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 to-blue-50/50" />
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-blue-500" />

      <div className="relative max-w-7xl mx-auto px-6">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            AquaIA c'est
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Un écosystème complet qui transforme votre façon de consommer
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group relative"
            >
              <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-emerald-200 h-full">
                {/* Image */}
                <div className="relative mb-6 overflow-hidden rounded-2xl">
                  <img
                    src={benefit.image}
                    alt={benefit.title}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${benefit.gradient} opacity-20 group-hover:opacity-30 transition-opacity duration-500`} />
                </div>

                {/* Content */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-emerald-700 transition-colors duration-300">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {benefit.description}
                  </p>
                </div>

                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-blue-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default WhatYouGet;